﻿//this store module handles user graph repo
import _ from "lodash-es";
import router, {ROUTE_NAMES} from "@/src/router";
import {GraphApi} from "@/src/model/GraphApi";
import {WorkspaceApi} from "@/src/model/WorkspaceApi";
import {DATA_API_TYPE, GRAPH_API_TYPE, WORKSPACE_API_TYPE} from "@/src/model/apiTypes";
import {DataApi} from "@/src/model/DataApi";
import {GRAPH_PERMISSION, WORKSPACE_PERMISSION} from "@/src/model/Permission";

const graphApi = new GraphApi()
const workspaceApi = new WorkspaceApi()
const dataApi = new DataApi()
import WorkspaceModel from "@/src/model/WorkspaceModel";

const LATEST_VIEWED_GRAPHS_KEY = 'latestViewedGraphs'

const updateLatestViewedGraphs = (graphId) => {
    if (!graphId) {
        return
    }
    const graphIdValue = String(graphId)
    const latestViewedGraphs = (JSON.parse(localStorage.getItem(LATEST_VIEWED_GRAPHS_KEY)) || []).map(id => String(id));
    if (!latestViewedGraphs.includes(graphIdValue)) {
        if (latestViewedGraphs.length > 3) {
            latestViewedGraphs.pop();
        }
    } else {
        latestViewedGraphs.splice(latestViewedGraphs.indexOf(graphIdValue), 1);
    }
    latestViewedGraphs.unshift(graphIdValue);
    localStorage.setItem(LATEST_VIEWED_GRAPHS_KEY, JSON.stringify(latestViewedGraphs));
}

const removeLatestViewedGraphs = (graphIds) => {
    if (!graphIds || graphIds.length === 0) {
        return
    }
    const graphIdsToRemove = graphIds.map(id => String(id))
    const latestViewedGraphs = (JSON.parse(localStorage.getItem(LATEST_VIEWED_GRAPHS_KEY)) || []).map(id => String(id));
    const filtered = latestViewedGraphs.filter(id => !graphIdsToRemove.includes(String(id)));
    localStorage.setItem(LATEST_VIEWED_GRAPHS_KEY, JSON.stringify(filtered));
}
export default {
    namespaced: true,
    state: {
        graphTree: [],
        selectedGraphId: null,
        unsavedGraph: false,
        activities: [],
        tables: [],
        versions: [],
        selectedVersion: 'draft',
        caseTables: [],
        datapools: [],
        workspaceUserPermissions: [],
        graphUserPermissions: [],
        graphResults: new Map(),
        graphTableOptions: [],
        templates: [],
        templateServiceError: null,
        currentGraph: null, // used to store the current graph data
        graphMap: new Map(),
        selectedMenu: null,
        graphOrder: {}
    },
    mutations: {
        setUnsavedGraph: (state, value) => {
            state.unsavedGraph = value
        },
        setSelectedVersion: (state, value) => {
            state.selectedVersion = value
        },

        setGraphId: (state, value) => {
            state.selectedGraphId = value
        },
        setGraphTree: (state, value) => {
            state.graphTree = value.map(w => {
                return new WorkspaceModel(null, w, state.graphMap)
            })
        },
        setGraphResults: (state, {spaceId, value}) => {
            state.graphResults.set(spaceId, value)
            // update results in graphMap for easy access
            value.forEach(({graph_id, result}) => {
                //update result propety in the graph object
                const graph = state.graphMap.get(graph_id)
                if (graph) {
                    graph.result = result
                }

            })
        },
        setTemplates: (state, value) => {
            if (value.error) {
                console.error('Error loading templates:', value.error)
                state.templateServiceError = value.error
                return
            }
            state.templates = value.map(ws => {
                return Object.assign({}, ws, {
                    key: ws.workspace_id,
                    label: ws.workspace_name,
                    leaf: false,
                    selectable: false,
                    children: ws.graphs.map(g => {
                        return Object.assign({}, g, {
                            key: ws.workspace_id + '_' + g.id,
                            label: g.name,
                            leaf: true,
                            selectable: true
                        })
                    })
                })
            })
        },
        setSelectedMenu: (state, value) => {
            state.selectedMenu = value
        },
        setGraphOrder: (state, {workspaceId, order}) => {
            state.graphOrder = {
                ...state.graphOrder,
                [workspaceId]: order,
            }
        }
    },
    actions: {
        testGrammar: async ({state}, testString) => {
            return await dataApi.exec(DATA_API_TYPE.PAC_GRAMMAR, {testString})
        },
        search: async ({state}, query) => {
            return await workspaceApi.exec(WORKSPACE_API_TYPE.SEARCH, {
                query
            })
        },
        loadWorkspaces: async ({state, dispatch, commit}) => {
            dispatch('loadTemplates')
            commit('setGraphTree', await workspaceApi.exec(WORKSPACE_API_TYPE.GET_WORKSPACES))
        },
        loadGraphResults: async ({state, commit}, spaceId) => {
            if (state.graphResults.has(spaceId)) {
                return
            }
            const result = await graphApi.exec(GRAPH_API_TYPE.GET_RESULTS, {
                spaceId
            });
            commit('setGraphResults', {spaceId, value: result})
        },
        addSpace: async ({dispatch}, data) => {
            await workspaceApi.exec(WORKSPACE_API_TYPE.ADD_WORKSPACE, data)
            dispatch('loadWorkspaces')
        },
        addGraph: async ({state, dispatch}, payload) => {
            const spaceId = payload?.spaceId ?? payload?.workspace_id ?? payload?.workspaceId
            const data = Object.assign({
                spaceId,
                pac_project_key: '',
                definition: {}
            }, payload)
            const graphResult = await graphApi.exec(GRAPH_API_TYPE.ADD_GRAPH, data)
            await dispatch('loadWorkspaces')
            const graph = state.graphMap.get(graphResult.id)
            await dispatch('setSelectedGraph', graph)
            dispatch('graphStore/toggleEditMode', true, {root: true})
        },
        updateWorkspace: async ({dispatch}, {workspaceId, data}) => {
            await workspaceApi.exec(WORKSPACE_API_TYPE.UPDATE_WORKSPACE, {spaceId: workspaceId, data})
            dispatch('loadWorkspaces')
        },
        cleanUpResults: async ({state, getters}, data) => {
            let graph = getters.getGraph;
            if (!graph) {
                return
            }
            await graphApi.exec(GRAPH_API_TYPE.CLEAN_UP_RESULTS, {
                graphId: state.selectedGraphId,
                data
            })
            Object.assign(graph, data)
        },
        reloadDatamodel: async ({state, getters}, data) => {
            await dataApi.exec(DATA_API_TYPE.RELOAD_DATAMODEL, {
                datapoolId: data.datapool_id,
                datamodelId: data.datamodel_id,
                leadingObjectTable: data.leading_object_table
            })
        },
        setSelectedSpace: async ({state, dispatch, commit, rootGetters}, spaceId) => {
            if (state.unsavedGraph) {
                return
            }
            state.workspaceUserPermissions = []
            const isEditMode = rootGetters['graphStore/getEditMode']
            if (isEditMode) {
                //toggle edit mode off to unlock the graph
                await dispatch('graphStore/toggleEditMode', {}, {root: true})
            }
            if (!spaceId) {
                router.push({name: ROUTE_NAMES.REPO})
                return
            }
            router.push({name: ROUTE_NAMES.REPO, params: {spaceId: spaceId}})
            await dispatch('loadUserWorkspacePermissions', spaceId)
            //do not await for it
            dispatch('loadGraphResults', spaceId)
        },
        getGraph: async ({state, getters}, {graphId}) => {
            try {
                return await graphApi.exec(GRAPH_API_TYPE.GET_GRAPH, {
                    graphId
                })
            } catch (error) {
                if (error?.response?.status === 404) {
                    router.replace({ name: ROUTE_NAMES.GRAPH_NOT_FOUND })
                    return null
                }
                throw error
            }
        },
        // refactor this mess
        setSelectedGraph: async ({state, dispatch, commit, rootGetters}, graph) => {
            state.graphUserPermissions = []
            if (state.unsavedGraph) {
                return
            }

            const isEditMode = rootGetters['graphStore/getEditMode']
            if (isEditMode) {
                //toggle edit mode off to unlock the graph
                await dispatch('graphStore/toggleEditMode', {}, {root: true})
            }

            if (!graph) {
                commit('setGraphId', null)
                return
            }
            commit('setGraphId', graph.id)
            updateLatestViewedGraphs(graph.id)
            // make sure graph is GraphModel instance
            await dispatch('updateStoredGraph', graph)
            await dispatch('loadUserGraphPermissions')
            const graphData = await dispatch('getGraph', {
                graphId: graph.id
            })
            if (!graphData) {
                return
            }
            //NOTE: don't remember why we were picking only some fields, monitor for error. In any case those fields are not enough
            // await dispatch('updateStoredGraph', _.pick(graphData, ['definition', 'pac_project_key', 'locked_by', 'locked_date', 'datapool_id', 'datamodel_id', 'notes']))
            await dispatch('updateStoredGraph', graphData)
            router.push({
                name: ROUTE_NAMES.GRAPH, params: {
                    graphId: graph.id
                }
            })
            await dispatch('loadVersions')

            commit('setSelectedVersion', 'draft')
        },
        updateStoredGraph: async ({state, getters}, data) => {
            const graphModel = state.graphMap.get(parseInt(data.id))
            state.currentGraph = Object.assign({}, state.currentGraph, data, {parent: graphModel.parent})
        },
        deleteWorkspace: async ({state, dispatch, commit}, ws) => {
            await workspaceApi.exec(WORKSPACE_API_TYPE.DELETE_WORKSPACE, {spaceId: ws.id})
            dispatch('loadWorkspaces')
        },
        deleteGraph: async ({state, dispatch, commit}, graphId) => {
            await graphApi.exec(GRAPH_API_TYPE.DELETE_GRAPH, {
                graphId
            })
            if (state.selectedGraphId === graphId) {
                commit('setGraphId', null)
            }
            removeLatestViewedGraphs([graphId])
            dispatch('loadWorkspaces')
        },
        deleteGraphs: async ({state, dispatch}, graphIds) => {
            await graphApi.exec(GRAPH_API_TYPE.DELETE_GRAPHS, graphIds)
            if (state.selectedGraphId && graphIds.includes(state.selectedGraphId)) {
                dispatch('setSelectedGraph', null)
            }
            removeLatestViewedGraphs(graphIds)
            dispatch('loadWorkspaces')
        },
        publishGraph: async ({state, dispatch}, description = '') => {
            await graphApi.exec(GRAPH_API_TYPE.PUBLISH_GRAPH, {
                graphId: state.selectedGraphId,
                description
            })
            dispatch('loadVersions')
        },
        cancelJob: async ({state}) => {
            return await graphApi.exec(GRAPH_API_TYPE.CANCEL_JOB, {
                graphId: state.selectedGraphId
            })
        },
        runGraphs: async ({}, graphIds) => {
            return await graphApi.exec(GRAPH_API_TYPE.RUN_JOBS, graphIds)
        },

        getDatapools: async ({state}) => {
            if (state.datapools.length == 0) {
                state.datapools = (await dataApi.exec(DATA_API_TYPE.GET_CONTAINERS)).map(label => {
                    return {id: label, name: label}
                })
            }
            return state.datapools
        },
        getDatamodels: async ({state}, datapoolId) => {
            const datapool = _.find(state.datapools, d => d.id == datapoolId)
            if (datapool && datapool.hasOwnProperty('datamodels')) {
                return datapool.datamodels
            }
            const datamodels = (await dataApi.exec(DATA_API_TYPE.GET_BLOBS, {containerName: datapoolId})).map(label => {
                return {id: btoa(label), name: label}
            })
            datapool.datamodels = datamodels
            return datamodels
        },
        getActivities: async ({state, getters}, {datapoolId, datamodelId}) => {
            return await dataApi.exec(DATA_API_TYPE.GET_ACTIVITIES, {
                datapoolId,
                datamodelId
            })
        },
        loadActivities: async ({state, getters, dispatch}) => {
            const graph = getters.getGraph
            state.activities = []
            state.tables = []
            if (graph && graph.container_name && graph.blob_name) {
                state.activities = await dataApi.exec(DATA_API_TYPE.GET_BLOB_ACTIVITIES, {
                    containerName: graph.container_name,
                    blobId: graph.blob_name,
                    graphId: graph.id
                })
                // state.tables = await dataApi.exec(DATA_API_TYPE.GET_TABLES, {
                //     datapoolId: graph.datapool_id,
                //     datamodelId: graph.datamodel_id
                // })
            }

        },
        loadVersions: async ({state}) => {
            state.versions = []
            state.versions = await graphApi.exec(GRAPH_API_TYPE.GET_VERSIONS, {
                graphId: state.selectedGraphId
            })
        },
        restoreVersion: async ({state, commit, dispatch}) => {
            const res = await graphApi.exec(GRAPH_API_TYPE.RESTORE_VERSION, {
                graphId: state.selectedGraphId,
                versionId: state.selectedVersion
            })
            await dispatch('updateStoredGraph', {definition: res.definition})
            commit('setSelectedVersion', 'draft')
        },
        //used in CaseTableSelector as 'load' + prop
        loadCaseTables: async ({state}, {datapoolId, datamodelId}) => {
            state.caseTables = []
            state.caseTables = (await dataApi.exec(DATA_API_TYPE.GET_BLOB_COLUMNS, {
                containerName: datapoolId,
                blobId: datamodelId
            })).map(label => {
                return {name: label}
            })
        },
        getWorkspacePermissions: async ({state}, workspaceId) => {
            return await workspaceApi.exec(WORKSPACE_API_TYPE.GET_PERMISSIONS, {
                spaceId: workspaceId
            })
        },
        updateWorkspacePermissions: async ({state}, {workspaceId, data}) => {
            return await workspaceApi.exec(WORKSPACE_API_TYPE.UPDATE_PERMISSIONS, {
                spaceId: workspaceId,
                data
            })
        },
        loadUserWorkspacePermissions: async ({state}, workspaceId) => {
            state.workspaceUserPermissions = (await workspaceApi.exec(WORKSPACE_API_TYPE.GET_USER_PERMISSIONS, {
                spaceId: workspaceId
            })).permissions
        },
        getGraphPermissions: async ({}, graphId) => {
            return await graphApi.exec(GRAPH_API_TYPE.GET_PERMISSIONS, {
                graphId: graphId
            })
        },
        updateGraphPermissions: async ({}, {graphId, data}) => {
            return await graphApi.exec(GRAPH_API_TYPE.UPDATE_PERMISSIONS, {
                graphId: graphId,
                data
            })
        },
        loadUserGraphPermissions: async ({state}) => {
            const res = (await graphApi.exec(GRAPH_API_TYPE.GET_USER_PERMISSIONS, {
                graphId: state.selectedGraphId
            }))
            state.graphUserPermissions = res.permissions
        },
        getTableColumns: async ({state, getters}, tableName) => {
            const tableObject = _.find(state.tables, table => table.name === tableName)
            if (!tableObject) {
                return []
            }

            if (tableObject && tableObject.hasOwnProperty('columns')) {
                return tableObject.columns
            }
            const graph = getters.getGraph
            const columns = await dataApi.exec(DATA_API_TYPE.GET_TABLE_COLUMNS, {
                datapoolId: graph.datapool_id,
                datamodelId: graph.datamodel_id,
                tableId: tableObject.id
            })
            tableObject.columns = columns
            return columns.sort()
        },
        validatePql: async ({getters}, pql) => {
            const graph = getters.getGraph
            if (graph && graph.datapool_id && graph.datamodel_id) {
                return await dataApi.exec(DATA_API_TYPE.VALIDATE_PQL, {
                    datapoolId: graph.datapool_id,
                    datamodelId: graph.datamodel_id,
                    pql
                })
            } else {
                return {
                    state: 'ERROR',
                    errors: ['Datamodel not set']
                }
            }
        },
        //used in CaseTableSelector as 'load' + prop
        loadGraphTableOptions: async ({state}, {datapoolId, datamodelId}) => {
            state.graphTableOptions = [{name: null}]
            const options = (await dataApi.exec(DATA_API_TYPE.GET_GRAPH_TABLE_OPTIONS, {
                datapoolId,
                datamodelId
            }))
            state.graphTableOptions = options
        },
        loadTemplates: async ({commit}) => {
            const templates = await dataApi.exec(DATA_API_TYPE.TEMPLATES)
            commit('setTemplates', templates)
        },
        moveGraphs: async ({state, dispatch}, data) => {
            await graphApi.exec(GRAPH_API_TYPE.MOVE_GRAPHS, data)
            await dispatch('loadWorkspaces')
        },
        loadGraphOrder: async ({state, commit}, workspaceId) => {
            if (state.graphOrder[workspaceId]) {
                return state.graphOrder[workspaceId]
            }

            const order = await graphApi.exec(GRAPH_API_TYPE.GET_GRAPH_ORDER, {
                workspaceId,
            })
            commit('setGraphOrder', {workspaceId, order})
            return order
        },
        updateGraphOrder: async ({commit}, {workspaceId, order}) => {
            commit('setGraphOrder', {workspaceId, order})
            await graphApi.exec(GRAPH_API_TYPE.UPDATE_GRAPH_ORDER, {
                workspaceId,
                order,
            })
        },
    },
    getters: {
        graphTree: state => state.graphTree || [],
        getGraph: (state) => {
            return state.currentGraph
        },
        getGraphDefinition: (state, getters) => {
            const graph = getters.getGraph;
            if (_.isEmpty(graph)) {
                return null
            }
            if (state.selectedVersion === 'draft') {
                return graph.definition
            }
            const foundVersion = _.find(state.versions, v => v.id == state.selectedVersion)
            if (!foundVersion) {
                return graph.definition
            }
            return foundVersion.definition
        },
        getVersions: (state) => {
            return state.versions
        },
        getSelectedVersion: (state) => {
            return state.selectedVersion
        },
        latestVersion: (state) => {
            return state.versions.length > 0 ? state.versions[0] : null
        },
        getActivityTables: state => {
            return state.activities
        },
        getActivities: (state) => {
            return state.activities.map(item => item.activity)
        },
        getTables: (state) => {
            return state.tables
        },
        getActivityTable: (state) => (name) => {
            const activity = _.find(state.activities, item => item.activity === name)
            return activity ? activity.table : ''
        },
        hasDatamodel: (state) => {
            return state.currentGraph && state.currentGraph.datamodel_id
        },
        //used in CaseTableSelector as 'get' + prop
        getCaseTables: state => state.caseTables,
        findDatapool: state => datamodelId => {
            for (let pool of state.datapools) {
                if (!pool.datamodels) {
                    continue
                }
                const found = _.find(pool.datamodels, model => model.id === datamodelId)
                if (found) {
                    return pool.id
                }
            }
            return null
        },
        getDatamodelLabel: state => datamodelId => {
            for (let pool of state.datapools) {
                if (!pool.datamodels) {
                    continue
                }
                const found = _.find(pool.datamodels, model => model.id === datamodelId)
                if (found) {
                    return found.name
                }
            }
            return null
        },
        getGraphUserPermissions: (state) => state.graphUserPermissions,
        getWorkspaceUserPermissions: (state) => state.workspaceUserPermissions,
        canEditGraph: (state, getters, rootState, rootGetters) => {
            const graph = getters.getGraph
            if (!graph) {
                return false
            }
            const workspace = graph.parent
            return rootGetters['userStore/userCan'](GRAPH_PERMISSION.EDIT_GRAPH, graph) || rootGetters['userStore/userCan'](WORKSPACE_PERMISSION.EDIT_ALL_GRAPHS, workspace)
        },
        getGraphResults: (state) => (spaceId) => {
            return state.graphResults.get(spaceId) || []
        },
        //used in CaseTableSelector as 'get' + prop
        getGraphTableOptions: state => leadingObjectTable => state.graphTableOptions[leadingObjectTable],
        selectedMenu: state => state.selectedMenu,
        graphOrderByWorkspace: state => workspaceId => state.graphOrder[workspaceId] || [],
        workspaceTree: (state, getters, rootState, rootGetters) => {
            //recursively filter the graphTree, keep only workspaces, remove grap leaves
            const userCan = rootGetters['userStore/userCan']
            const filterWorkspaces = (tree) => {
                return tree.filter(item => item instanceof WorkspaceModel).map(item => {
                    if (item.children && item.children.length > 0) {
                         let children = filterWorkspaces(item.children)
                         return Object.assign({}, item, {
                             children,
                             leaf: children.length === 0,
                             selectable: userCan(WORKSPACE_PERMISSION.EDIT_WORKSPACE, item)
                        })
                    }
                    return Object.assign({}, item, {
                        children: [],
                        leaf: true,
                        styleClass: userCan(WORKSPACE_PERMISSION.EDIT_WORKSPACE, item) ? '' : 'bg-gray-100 cursor-not-allowed',
                        disabled: !userCan(WORKSPACE_PERMISSION.EDIT_WORKSPACE, item),
                        selectable: userCan(WORKSPACE_PERMISSION.EDIT_WORKSPACE, item)
                    })
                })
            }
            return filterWorkspaces(_.cloneDeep(state.graphTree))
        }
    }
}
